<template>
  <div class="rounded-xl border border-gray-200 overflow-hidden bg-white">
    <div class="p-4">

      <!-- Header -->
      <div class="tw-flex items-center justify-between">
        <div class="tw-flex items-center gap-2">
          <svg class="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          <span class="text-base font-semibold text-gray-900">{{ $t('traction_tips_title') }}</span>
        </div>
      </div>
      <p class="text-sm font-medium text-primary mt-0.5">{{ completedCount }}/{{ allSteps.length }} {{ $t('traction_tips_completed') }}</p>

      <!-- Overall progress bar -->
      <div class="mt-2 mb-4 w-full bg-gray-200 rounded-full h-2">
        <div
          class="bg-primary h-2 rounded-full transition-all duration-500"
          :style="{ width: overallProgress + '%' }"
        />
      </div>

      <!-- Tips -->
      <div class="space-y-2">
        <div v-for="tip in tips" :key="tip.id">

          <!-- Tip card -->
          <div :class="['rounded-lg border transition-colors', tip.completed ? 'border-gray-100 bg-gray-50' : 'bg-white border-gray-200']">

            <!-- Header row -->
            <div
              @click.stop="onTipClick(tip)"
              class="tw-flex items-start gap-3 p-3 cursor-pointer"
            >
              <svg v-if="tip.completed" class="mt-0.5 h-5 w-5 shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <svg v-else class="mt-0.5 h-5 w-5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke-width="2"/>
              </svg>

              <div class="flex-1 min-w-0">
                <div class="tw-flex items-start justify-between gap-2">
                  <p :class="['text-sm font-medium leading-snug mb-0', tip.completed ? 'text-gray-400 line-through' : 'text-gray-900']">
                    {{ tip.title }}
                  </p>
                  <div class="tw-flex items-center gap-2 shrink-0">
                    <svg
                      v-if="tip.subSteps"
                      :class="['h-4 w-4 text-gray-400 transition-transform duration-200 mt-0.5', expandedTips[tip.id] ? 'rotate-180' : '']"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>
                <span :class="['text-xs font-medium mt-0.5', tip.impact === 'High' ? 'text-primary' : 'text-gray-400']">
                  {{ $t('traction_tips_impact_' + tip.impact.toLowerCase()) }}
                </span>
                <p class="mt-0.5 text-xs text-gray-500 leading-snug">{{ tip.description }}</p>
                <div v-if="!tip.completed && tip.progress != null && !tip.subSteps" class="mt-2 tw-flex items-center gap-2">
                  <div class="flex-1 bg-gray-200 rounded-full h-1.5">
                    <div class="bg-primary h-1.5 rounded-full transition-all" :style="{ width: tip.progress + '%' }"/>
                  </div>
                  <span class="text-xs text-gray-400 shrink-0">{{ tip.progress }}%</span>
                </div>
              </div>
            </div>

            <!-- Sub-steps (collapsible, inside the card) -->
            <div v-if="tip.subSteps && expandedTips[tip.id]" class="border-t border-gray-100 px-3 pb-3 pt-2 tw-flex flex-col gap-1">
              <template v-for="step in tip.subSteps" :key="step.id">

                <!-- Actor list -->
                <template v-if="step.type === 'actor-list'">
                  <div v-if="step.actors && step.actors.length" class="tw-flex items-center gap-2.5 px-2 pt-1 pb-0.5">
                    <svg :class="['h-4 w-4 shrink-0', step.actors.every(a => a.is_complete) ? 'text-primary' : 'text-gray-400']" fill="none" stroke="currentColor"
                         viewBox="0 0 24 24">
                      <path v-if="step.actors.every(a => a.is_complete)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      <circle v-else cx="12" cy="12" r="10" stroke-width="2"/>
                    </svg>
                    <span :class="['text-xs font-medium', step.actors.every(a => a.is_complete) ? 'text-gray-400 line-through' : 'text-gray-500']">
                      {{
                        step.actors.length !== 1 ? $t('traction_tips_complete_following_profiles', { actor: actorLabel }) : $t('traction_tips_complete_following_profile', { actor: actorLabel })
                      }}
                    </span>
                  </div>
                  <div
                    v-for="actor in step.actors"
                    :key="actor.id"
                    @click.stop="actor.click()"
                    :class="[
                      'tw-flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors cursor-pointer',
                      actor.is_complete ? 'opacity-60' : 'hover:bg-gray-50'
                    ]"
                  >
                    <div class="h-6 w-6 shrink-0 rounded-full bg-gray-100 tw-flex items-center justify-center overflow-hidden">
                      <img v-if="actor.logo" :src="actor.logo" :alt="actor.name" class="h-full w-full object-cover"/>
                      <span v-else class="text-xs font-medium text-gray-500">{{ actor.name ? actor.name[0].toUpperCase() : '?' }}</span>
                    </div>
                    <span :class="['flex-1 text-xs font-medium truncate', actor.is_complete ? 'text-gray-400 line-through' : 'text-gray-700']">
                      {{ actor.name || $t('traction_tips_unnamed_actor') }}
                    </span>
                    <svg v-if="actor.is_complete" class="h-3.5 w-3.5 shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <svg v-else class="h-3.5 w-3.5 shrink-0 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </template>

                <!-- Standard sub-step -->
                <div
                  v-else
                  @click.stop="!step.locked && step.click && step.click()"
                  :class="[
                    'tw-flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors',
                    step.locked ? 'opacity-40 cursor-not-allowed' :
                      step.completed ? 'cursor-default' :
                        'hover:bg-gray-50 cursor-pointer'
                  ]"
                >
                  <svg v-if="step.completed" class="h-4 w-4 shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <svg v-else-if="step.locked" class="h-4 w-4 shrink-0 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                  <svg v-else class="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke-width="2"/>
                  </svg>
                  <p :class="['text-xs font-medium leading-snug flex-1', step.completed ? 'text-gray-400 line-through' : step.locked ? 'text-gray-400' : 'text-gray-700']">
                    {{ step.title }}
                  </p>
                </div>

              </template>
            </div>

          </div>

        </div>
      </div>

      <!-- View More Tips button -->
      <!--      <button
              class="mt-3 w-full tw-flex items-center justify-center gap-2 rounded-lg border border-primary bg-transparent px-4 py-2 text-sm font-medium text-primary hover:bg-primary-lightest transition-colors"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m1.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
              {{ $t('traction_tips_view_more') }}
            </button>-->

    </div>
  </div>
</template>

<script>
  import { getUserTractionTips } from '@/api/user'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import UserMixin from '@/util/UserMixin'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'

  export default {
    name: 'TractionTips',
    data () {
      return {
        tractionData: null,
        expandedTips: {},
      }
    },
    beforeUnmount () {
      this.$bus.off('fetchTractionTips')
    },
    async mounted () {
      try {
        this.tractionData = await getUserTractionTips()
      } catch (e) { /* silently ignore */
      }

      // Auto-expand tips that have sub-steps and are not complete
      this.$nextTick(() => {
        const expanded = {}
        this.tips.forEach(tip => {
          if (tip.subSteps && !tip.completed) {
            expanded[tip.id] = true
          }
        })
        this.expandedTips = expanded
      })

      this.$bus.on('fetchTractionTips', async () => {
        try {
          this.tractionData = await getUserTractionTips()
        } catch (e) { /* silently ignore */
        }
      })
    },
    emits: ['open-update-profile-modal'],
    mixins: [UserMixin, TranslationsMixin],
    methods: {
      onTipClick (tip) {
        if (tip.subSteps) {
          const nowExpanded = !this.expandedTips[tip.id]
          this.expandedTips = { ...this.expandedTips, [tip.id]: nowExpanded }
          trackMatomoEvent(
            MATOMO_EVENT_CATEGORIES.TRACTION_TIPS,
            nowExpanded ? MATOMO_EVENT_ACTIONS.EXPAND_TIP : MATOMO_EVENT_ACTIONS.COLLAPSE_TIP,
            tip.title,
          )
        } else if (tip.click) {
          trackMatomoEvent(MATOMO_EVENT_CATEGORIES.TRACTION_TIPS, MATOMO_EVENT_ACTIONS.TIP_ACTION, tip.title)
          tip.click()
        }
      },
    },
    computed: {
      profile () {
        return this.$store.state.user.profile
      },
      tips () {
        const d = this.tractionData

        // Tip 1: profile
        const hasPhoto = d ? d.profileProgress.hasPhoto : !!(this.profile && this.profile.photo_url)
        const hasBio = d ? d.profileProgress.hasBio : !!(this.profile && this.profile.bio)
        const profileComplete = d ? d.profileComplete : (hasPhoto && hasBio)

        // Tip 2: announcement
        const hasPostedAnnouncement = d ? d.hasPostedAnnouncement : false

        // Tip 3: actor profile
        const claims = (this.profile && this.profile.claims) || []
        const nonProductClaims = claims.filter(c => c.actor_type !== 'Product')
        const hasActor = d ? d.hasActor : nonProductClaims.length > 0
        const actorProfileComplete = d ? d.actorProfileComplete : false
        const actorCounts = d ? d.actorProfileCounts : null
        const actorListItems = d ? d.actors : nonProductClaims.map(c => ({
          id: c.id,
          name: c.name,
          logo: c.logo || c.logo_url || null,
          is_complete: c.is_complete || false,
        }))

        // Tip 4: joined a community
        const hasJoinedCommunity = d ? d.hasJoinedCommunity : (this.$store.getters.joinedCommunityIds || []).length > 0

        // Tip 5: conversations
        const conversationPartners = d ? d.conversationPartners : 0
        const conversationGoal = d ? d.conversationGoal : 3
        const conversationProgress = Math.min(Math.round((conversationPartners / conversationGoal) * 100), 100)

        // Tip 7: testimonials sent
        const testimonialsSent = d ? d.testimonialsSent : 0
        const testimonialsGoal = d ? d.testimonialsGoal : 1
        const testimonialsComplete = testimonialsSent >= testimonialsGoal

        return [
          {
            id: 1,
            title: this.$t('traction_tips_complete_profile'),
            description: this.$t('traction_tips_complete_profile_description'),
            completed: profileComplete,
            progress: profileComplete ? null : Math.round(((hasPhoto ? 1 : 0) + (hasBio ? 1 : 0)) / 2 * 100),
            impact: 'High',
            click: () => {
              if (!profileComplete) {
                trackMatomoEvent(MATOMO_EVENT_CATEGORIES.TRACTION_TIPS, MATOMO_EVENT_ACTIONS.TIP_ACTION, 'complete_profile')
                this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
              }
            },
          },
          {
            id: 2,
            title: this.$t('traction_tips_build_actor_presence', { actor: this.actorLabel }),
            description: actorCounts && actorCounts.total > 0
              ? this.$t('traction_tips_actor_profiles_complete', { completed: actorCounts.completed, total: actorCounts.total, actor: this.actorLabel })
              : this.$t('traction_tips_build_actor_presence_description', { actor: this.actorLabel }),
            completed: hasActor && actorProfileComplete,
            progress: null,
            impact: 'High',
            subSteps: [
              {
                id: '2a',
                title: this.$t('traction_tips_create_actor', { actor: this.actorLabel }),
                completed: hasActor,
                locked: false,
                progress: null,
                click: () => {
                  if (!hasActor) {
                    trackMatomoEvent(MATOMO_EVENT_CATEGORIES.TRACTION_TIPS, MATOMO_EVENT_ACTIONS.TIP_ACTION, 'create_actor')
                    this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
                  }
                },
              },
              {
                id: '2b',
                type: 'actor-list',
                completed: hasActor && actorProfileComplete,
                locked: !hasActor,
                actors: actorListItems.map(actor => ({
                  ...actor,
                  click: () => {
                    trackMatomoEvent(MATOMO_EVENT_CATEGORIES.TRACTION_TIPS, MATOMO_EVENT_ACTIONS.TIP_ACTION, 'edit_actor:' + (actor.name || actor.id))
                    this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
                      component: 'actor-edit',
                      metaData: { id: actor.id },
                    })
                  },
                })),
              },
            ],
          },
          this.$store.getters.hasAccessToCommunities && {
            id: 3,
            title: this.$t('traction_tips_join_community'),
            description: this.$t('traction_tips_join_community_description'),
            completed: hasJoinedCommunity,
            progress: hasJoinedCommunity ? null : 0,
            impact: 'High',
            click: () => {
              if (!hasJoinedCommunity) {
                trackMatomoEvent(MATOMO_EVENT_CATEGORIES.TRACTION_TIPS, MATOMO_EVENT_ACTIONS.TIP_ACTION, 'join_community')
                this.$router.push('/communities')
              }
            },
          },
          this.$store.getters.hasAccessToCommunities && {
            id: 5,
            title: this.$t('traction_tips_post_announcement'),
            description: this.$t('traction_tips_post_announcement_description'),
            completed: hasPostedAnnouncement,
            progress: hasPostedAnnouncement ? null : 0,
            impact: 'Medium',
            click: () => {
              if (!hasPostedAnnouncement) {
                trackMatomoEvent(MATOMO_EVENT_CATEGORIES.TRACTION_TIPS, MATOMO_EVENT_ACTIONS.TIP_ACTION, 'post_announcement')
                this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.MANAGE_ANNOUNCEMENT)
              }
            },
          },
          this.$store.getters.hasAccessToCommunities && {
            id: 6,
            title: this.$t('traction_tips_talk_to_people'),
            description: this.$t('traction_tips_talk_to_people_description'),
            completed: conversationPartners >= conversationGoal,
            progress: conversationPartners >= conversationGoal ? null : conversationProgress,
            impact: 'Medium',
          },
          {
            id: 7,
            title: this.$t('traction_tips_recommend_companies', { actors: this.actorsLabel }),
            description: this.$t('traction_tips_recommend_companies_description', { actors: this.actorsLabel.toLowerCase(), sent: testimonialsSent, goal: testimonialsGoal }),
            completed: testimonialsComplete,
            progress: testimonialsComplete ? null : Math.min(Math.round((testimonialsSent / testimonialsGoal) * 100), 100),
            impact: 'Medium',
          },
        ].filter(Boolean)
      },
      allSteps () {
        const steps = []
        this.tips.forEach(tip => {
          if (tip.subSteps) {
            tip.subSteps.forEach(step => {
              if (step.type === 'actor-list') {
                // Count each actor individually
                if (step.actors && step.actors.length) {
                  step.actors.forEach(a => steps.push({ completed: a.is_complete }))
                }
              } else {
                steps.push(step)
              }
            })
          } else {
            steps.push(tip)
          }
        })
        return steps
      },
      completedCount () {
        return this.allSteps.filter(s => s.completed).length
      },
      overallProgress () {
        return Math.round((this.completedCount / this.allSteps.length) * 100)
      },
    },
  }
</script>
